/**
 * Authentication state for the app (P0-TR4).
 *
 * Holds the signed-in partner and nothing sensitive: the access token lives in
 * `tokenStore` (memory only) and the refresh token in an httpOnly cookie the
 * browser will not show us.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api, refreshAccessToken } from '../../api/client.js';
import { clearAccessToken, onUnauthorized, setAccessToken } from '../../api/tokenStore.js';

export interface AuthUser {
  id: number;
  fullName: string;
  phone: string;
  email: string | null;
  role: 'admin' | 'partner' | 'staff';
  language: 'bn' | 'en';
  isActive: boolean;
}

interface LoginResponse {
  success: true;
  data: { accessToken: string; user: AuthUser };
}

export interface AuthState {
  user: AuthUser | null;
  /** True until the initial silent refresh settles, so routes do not flash. */
  isLoading: boolean;
  login: (phone: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = useCallback(async (phone: string, password: string) => {
    const response = await api.post<LoginResponse>('/auth/login', { phone, password });
    setAccessToken(response.data.data.accessToken);
    setUser(response.data.data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      // Server-side revocation: without this, logging out would only forget
      // the token locally while the session stayed alive for 30 days.
      await api.post('/auth/logout');
    } catch {
      /*
       * Deliberately swallowed. From the partner's point of view, tapping
       * logout on a phone with no signal has to sign them out of that phone —
       * surfacing an error, or rejecting so the caller must handle it, would
       * leave them looking at a screen that still says they are logged in.
       * The refresh token expires on its own, and the access token dies within
       * 15 minutes, so the untold server keeps no usable session for long.
       */
    } finally {
      clearAccessToken();
      setUser(null);
    }
  }, []);

  /**
   * Restores the session on load (P0-BR3).
   *
   * The access token is gone after a refresh or a browser restart, but the
   * httpOnly refresh cookie survives 30 days — so the partner who closed the
   * app yesterday is simply still logged in.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await refreshAccessToken();
        const me = await api.get<{ success: true; data: AuthUser }>('/auth/me');
        if (!cancelled) setUser(me.data.data);
      } catch {
        // No usable cookie: not an error, just nobody signed in yet.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /** The interceptor tells us when a refresh finally failed mid-session. */
  useEffect(() => onUnauthorized(() => setUser(null)), []);

  const value = useMemo<AuthState>(() => ({ user, isLoading, login, logout }), [user, isLoading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
